"use strict";

import $ from "jquery";
import adjustPieChartCenterText from "./adjust_pie_chart_title";
import adjustStackedChartOrder from "./adjust_stacked_chart_order";

function cardContextFromChart(chart) {
  if (!chart) {
    return {};
  }

  const chartOptions = (chart.userOptions && chart.userOptions.chart) || {};
  const $renderTo = chart.renderTo ? $(chart.renderTo) : $();
  const $card = $renderTo.closest("[data-card]");
  let cardTitle = $card.data("modal-title");

  if (!cardTitle && $card.length) {
    cardTitle = $card.find(".card-title").first().text().trim();
  }

  return {
    card_id: chartOptions.card_id || $card.data("card-id"),
    chart_id: chartOptions.id,
    chart_template_type: $card.data("chart-template-type"),
    card_title: cardTitle,
    card_expanded: $card.data("card-expanded"),
    page_url: window.location.href
  };
}

function chartFromLegendClickTarget(that) {
  return (that && that.chart) || null;
}

function notifyChartError(chart, error, source) {
  if (!window.airbrake || !error) {
    return;
  }

  window.airbrake.notify({
    error: error,
    params: {
      highcharts: {
        source: source,
        card: cardContextFromChart(chart)
      }
    }
  });
}

const legendClickAnalytics = function (that, addCurrentClickToVisible = false) {
  try {
    const {
      chart,
      index,
    } = that;
    const $container = $(chart.container);
    const {
      card_id,
      id
    } = chart.userOptions.chart;

    const item_id = id;
    const item_type = "chart";
    const clickedSeries = chart.series.find(e => e.index === index);
    const visibleSeries = chart.series.filter(e => e.visible).map(e => e.name);
    if (addCurrentClickToVisible) {
      visibleSeries.push(clickedSeries.name);
    }
    const extras = {
      card_id,
      visibleSeries,
      clickedSeries: clickedSeries.name,
    };
    const messageUrl = "/analytics_messages/chart_legend_click";

    const msg = {
      url: messageUrl,
      type: "post",
      data: { item_type: item_type, item_id: item_id, outbound_extras: extras },
      dataType: "json",
      success: function () { $container.data("analyticsMessage", "success"); },
      error: function () { $container.data("analyticsMessage", "error"); }
    };

    $.ajax(msg);
  } catch(err) {
    // eslint-disable-next-line
    console.log(err);
    notifyChartError(chartFromLegendClickTarget(that), err, "legendClickAnalytics");
  }
};

module.exports = {
  // Clicking a legend entry isolates it: the first click hides every other entry, and
  // clicking again once the others are hidden brings them all back. Cancelling Highcharts'
  // own visibility toggle — by returning false — is what makes that possible, so this must
  // be reached through `alternateHighchartsLegendItemClick` below, on the one legend event
  // that still runs the default afterwards.
  //
  // `this` is the clicked entry: a series on most charts, a point on a pie.
  alternateHighchartsLegendClick: function () {
    const seriesIndex = this.index;
    if (this.chart) {
      const series = this.chart.series;

      let allVisible = true;
      $.each(series, function (a, e) {
        allVisible = allVisible && e.visible;
      });
      const someVisible = !allVisible;

      if (allVisible) {
        for (let i = 0; i < series.length; i++)
        {
          if (series[i].index != seriesIndex)
          {
            series[i].visible ? series[i].hide() : series[i].show();
          }
        }

        adjustStackedChartOrder(this.chart);
        legendClickAnalytics(this);

        return false;
      }

      if (someVisible) {
        if (!this.visible) {
          adjustStackedChartOrder(this.chart);
          legendClickAnalytics(this, true);

          return true;
        } else {
          let allOthersHidden = true;
          for (let i = 0; i < series.length; i++)
          {
            if (series[i].index != seriesIndex)
            {
              allOthersHidden = allOthersHidden && !series[i].visible;
            }
          }

          if (allOthersHidden) {
            $.each(series, function (a, e) {
              e.show();
            });

            adjustStackedChartOrder(this.chart);
            legendClickAnalytics(this);

            return false;
          }

          adjustStackedChartOrder(this.chart);
          legendClickAnalytics(this, true);

          return true;
        }
      }
    } else {
      const seriesObject = this.series;
      const series = this.series.points;

      let allVisible = true;
      $.each(series, function (a, e) {
        allVisible = allVisible && e.visible;
      });
      const someVisible = !allVisible;

      if (allVisible) {
        for (let i = 0; i < series.length; i++)
        {
          if (series[i].index != seriesIndex)
          {
            series[i].visible ? series[i].setVisible(false) : series[i].setVisible(true);
          }
        }
        setTimeout(function () { adjustPieChartCenterText(seriesObject.chart);}, 0);
        this.setState();

        adjustStackedChartOrder(seriesObject.chart);
        return false;
      }

      if (someVisible) {
        if (!this.visible) {
          setTimeout(function () { adjustPieChartCenterText(seriesObject.chart);}, 0);
          this.setState();

          return true;
        } else {
          let allOthersHidden = true;
          for (let i = 0; i < series.length; i++)
          {
            if (series[i].index != seriesIndex)
            {
              allOthersHidden = allOthersHidden && !series[i].visible;
            }
          }

          if (allOthersHidden) {
            $.each(series, function (a, e) {
              e.setVisible(true);
            });
            setTimeout(function () { adjustPieChartCenterText(seriesObject.chart);}, 0);
            this.setState();

            adjustStackedChartOrder(seriesObject.chart);
            return false;
          }

          setTimeout(function () { adjustPieChartCenterText(seriesObject.chart);}, 0);
          this.setState();

          adjustStackedChartOrder(seriesObject.chart);
          return true;
        }
      }
    }
  },
  // Bind this to `legend.events.itemClick`, not to `legendItemClick`. Highcharts fires
  // `itemClick` with its own visibility toggle attached as the default, so a handler
  // returning false suppresses it; the legacy `legendItemClick` is fired afterwards with
  // no default attached, by which time the entry has already been toggled and returning
  // false cancels nothing. The clicked entry arrives on the event rather than as `this`.
  alternateHighchartsLegendItemClick: function (event) {
    const item = event && event.legendItem;

    return item ? module.exports.alternateHighchartsLegendClick.call(item) : undefined;
  },
  alertTest: function () {
    alert("works");
  },
  cardContextFromChart,
  notifyChartError
};
